import { nonDraggablesTypeSlide } from "@/lib/constants/slides";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addElement } from "@/store/slideEditorSlice";
import type { ArrowType, LineElement, LineStyle, ShapeElement } from "@ai-teach/types";
import toast from "react-hot-toast";

export interface LineConfig {
  lineStyle: LineStyle;
  startArrow?: ArrowType;
  endArrow?: ArrowType;
}
export const shapeSizeMap: Record<ShapeElement["shapeType"], { width: number; height: number }> = {
  rectangle: { width: 200, height: 200 },
  circle: { width: 200, height: 200 },
  triangle: { width: 200, height: 180 },
  diamond: { width: 180, height: 180 },
  hexagon: { width: 200, height: 200 },
  star: { width: 200, height: 200 },
  heart: { width: 200, height: 200 },
  cross: { width: 180, height: 180 },
  sun: { width: 200, height: 200 },
  arrow: { width: 220, height: 120 },
  "double-arrow": { width: 240, height: 120 },
  tunnel: { width: 180, height: 200 },
};

export function useAddShapeElement() {
  const dispatch = useAppDispatch();
  const { slide, canvasSize } = useAppSelector((state) => state.slideEditor.present);

  const slideType = slide?.type;
  const knowledgeArea = slide?.knowledgeArea;

  const centeredPosition = (width: number, height: number) => {
    const canvasWidth = canvasSize.height * canvasSize.aspectRatio;
    return {
      x: Math.round((canvasWidth - width) / 2),
      y: Math.round((canvasSize.height - height) / 2),
    };
  };

  const handleAddShapeItem = (shape: ShapeElement["shapeType"]) => {
    if (!slideType) return;
    if (
      !knowledgeArea ||
      (nonDraggablesTypeSlide[slideType] as readonly string[])?.includes(knowledgeArea)
    ) {
      toast.error("This slide type doesn't support shape element", {
        id: "slide-shape-limit",
      });
      return;
    }

    const defaultProps = {
      type: "shape" as const,
      position: centeredPosition(shapeSizeMap[shape].width, shapeSizeMap[shape].height),
      fill: "#000000",
      border: {
        lineStyle: "none",
        stroke: "transparent",
        strokeWidth: 3,
        cornerRadius: 0,
      },
    };

    const size = shapeSizeMap[shape];
    if (size) {
      dispatch(
        addElement({
          ...defaultProps,
          size,
          shapeType: shape,
        } as ShapeElement)
      );
    }
  };

  const handleAddLineItem = (config: LineConfig) => {
    if (!slideType) return;

    if (
      !knowledgeArea ||
      (nonDraggablesTypeSlide[slideType] as readonly string[])?.includes(knowledgeArea)
    ) {
      toast.error("This slide type doesn't support line element", {
        id: "slide-line-limit",
      });
      return;
    }

    dispatch(
      addElement({
        type: "line" as const,
        position: centeredPosition(200, 100),
        size: { width: 200, height: 100 },
        startX: 100,
        startY: 100,
        endX: 300,
        endY: 100,
        stroke: "#000000",
        strokeWidth: 3,
        lineStyle: config.lineStyle,
        startArrow: config.startArrow || "none",
        endArrow: config.endArrow || "none",
        opacity: 1,
        border: {
          lineStyle: "none",
          stroke: "transparent",
          strokeWidth: 3,
          cornerRadius: 0,
        },
      } as LineElement)
    );
  };

  return {
    handleAddShapeItem,
    handleAddLineItem,
  };
}
